// Shared eye-guidance primitives for the v2 demo:
//   - arrowFrom(svg, from, to, opts): inline SVG <path> with a curved
//     tail + stroke-dashoffset draw-in animation. Returns the node so
//     the caller decides where to mount it.
//   - stepBadge(n, label, opts): a circular numbered badge + label
//     unit. Used as the anchor target for an arrow's tail.
//   - ensureArrowMarker(svg): one-shot installer for the shared
//     <marker> arrowhead inside a given SVG's <defs>.
//
// All three lean on the existing el()/svg() helpers exported from
// demo.js. Kept dependency-free here by re-implementing minimal
// versions — demo.js owns the canonical ones.

const SVG_NS = "http://www.w3.org/2000/svg";

interface SvgAttrs { [k: string]: string | number; }

function $svg(
  tag: string,
  attrs: SvgAttrs = {},
  children: (Node | null | undefined)[] = [],
): SVGElement {
  const e = document.createElementNS(SVG_NS, tag) as SVGElement;
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  for (const c of children) if (c) e.appendChild(c);
  return e;
}

interface ElAttrs {
  class?: string;
  html?: string;
  text?: string;
  [k: string]: string | number | undefined;
}

function $el(
  tag: string,
  attrs: ElAttrs = {},
  children: (Node | null | undefined)[] = [],
): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    if (k === "class") e.className = String(v);
    else if (k === "html") e.innerHTML = String(v);
    else if (k === "text") e.textContent = String(v);
    else e.setAttribute(k, String(v));
  }
  for (const c of children) if (c) e.appendChild(c);
  return e;
}

const ARROW_MARKER_ID = "demo-eye-arrow";

// Install the shared arrowhead marker into an SVG's <defs> if it isn't
// already there. Safe to call repeatedly.
export function ensureArrowMarker(svgRoot: SVGElement | null | undefined): void {
  if (!svgRoot) return;
  if (svgRoot.querySelector(`#${ARROW_MARKER_ID}`)) return;
  let defs = svgRoot.querySelector("defs") as SVGDefsElement | null;
  if (!defs) {
    defs = $svg("defs") as SVGDefsElement;
    svgRoot.insertBefore(defs, svgRoot.firstChild);
  }
  const marker = $svg("marker", {
    id: ARROW_MARKER_ID,
    viewBox: "0 0 10 10",
    refX: "9", refY: "5",
    markerWidth: "5", markerHeight: "5",
    orient: "auto-start-reverse",
  });
  marker.appendChild($svg("path", {
    d: "M 0 0 L 10 5 L 0 10 z",
    fill: "var(--brand-2)",
  }));
  defs.appendChild(marker);
}

export interface Point2D {
  x: number;
  y: number;
}

export interface ArrowOpts {
  /** How much the tail bows. Default 80. Positive bends "left". */
  curve?: number;
  /** Animation delay in ms. */
  delay?: number;
  /** Extra class on the <path>. */
  className?: string;
}

// Build a curved-tail arrow from `from` to `to` (both {x, y} in the
// containing SVG's coordinate space). Returns a <path> element with
// a draw-in animation (controlled by adding .is-drawn on a parent or
// on the path itself).
export function arrowFrom(from: Point2D, to: Point2D, opts: ArrowOpts = {}): SVGElement {
  const {
    curve = 80,
    delay = 0,
    className = "",
  } = opts;
  // Cubic Bezier with a single control point: midpoint of the chord
  // shifted perpendicular by `curve`. (We use one control point for a
  // smooth single-arc tail rather than the more flexible 2-control
  // Bezier, which over-wiggles for short arrows.)
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.max(1, Math.hypot(dx, dy));
  // Perpendicular unit vector (rotate the unit tangent 90°).
  const px = -dy / len;
  const py = dx / len;
  const cx = mx + px * curve;
  const cy = my + py * curve;
  const d = `M ${from.x.toFixed(1)},${from.y.toFixed(1)} Q ${cx.toFixed(1)},${cy.toFixed(1)} ${to.x.toFixed(1)},${to.y.toFixed(1)}`;
  return $svg("path", {
    // F17 Slice B: legacy `.demo-arrow` rule (stroke + width + fill)
    // inlined here. The Read scene's `.demo-read-arrows .demo-arrow`
    // selector still overrides via descendant in pilot-engine.css.
    class: `demo-arrow [stroke:oklch(98.6%_0.002_67.8/0.7)] [stroke-width:1.5] fill-none${className ? " " + className : ""}`,
    d,
    fill: "none",
    stroke: "var(--brand-2)",
    "stroke-width": "1.6",
    "stroke-linecap": "round",
    pathLength: "1",
    "marker-end": `url(#${ARROW_MARKER_ID})`,
    style: `--delay: ${delay}ms;`,
  });
}

export interface StepBadgeOpts {
  /** Which side of the badge the label sits on. Default "right". */
  align?: "left" | "right";
  /** Animation delay in ms. */
  delay?: number;
  /** Extra class on the container. */
  className?: string;
}

// A numbered reading-order badge paired with a single-line label.
// Builds an HTML element (not SVG) — overlay on whatever scene root
// the caller chooses to mount in.
export function stepBadge(n: number, label: string, opts: StepBadgeOpts = {}): HTMLElement {
  const { align = "right", delay = 0, className = "" } = opts;
  // F17 Slice B: Tailwind utilities replace the `.demo-eye-badge*`
  // rules from pilot-engine.css. The legacy hook classes
  // (`.demo-eye-badge`, `.demo-port-gutter-badge .demo-eye-badge-num`
  // override) stay because the Port scene applies the gutter-badge
  // descendant override class to inherit a brand-accent recolour.
  const cls = `demo-eye-badge demo-eye-badge-${align} inline-flex items-center gap-2${className ? " " + className : ""}`;
  const numEl = $el("span", {
    class:
      "demo-eye-badge-num inline-flex size-[22px] rounded-full items-center justify-center font-mono text-[11px] font-semibold text-taupe-50 [border:1px_solid_oklch(98.6%_0.002_67.8/0.5)] [background:oklch(98.6%_0.002_67.8/0.08)]",
    text: String(n),
  });
  const labelEl = $el("span", {
    class:
      "demo-eye-badge-label font-mono text-[11px] tracking-[0.04em] text-taupe-300",
    text: label,
  });
  const children = align === "left" ? [labelEl, numEl] : [numEl, labelEl];
  return $el("div", {
    class: cls,
    style: `--delay: ${delay}ms;`,
  }, children);
}
